import { FuturePEMultipleContext } from './FuturePEMultipleContext';
import { useCallback } from 'react';

export type FuturePEMultipleCalculationInput = {
  discountRatePercentage: number;
  futurePE: number;
  profitMarginPercentage: number;
  revenueGrowthRatePercentage: number;
  timeFrame: number;
};

export type FuturePEMultipleFairValueCalculationInput = Omit<
  FuturePEMultipleCalculationInput,
  'revenueGrowthRatePercentage'
> & {
  fairValue: number;
};

export type FuturePEMultipleCalculationOutput = {
  fairValue: number;
  discountRatePercentage: number;
  futureSharesOutstanding: number;
  priceEarningsMultiple: number;
  profitMarginPercentage: number;
  revenuePercentage: number;
  timePeriod: number;
  sharesOutstandingGrowthRate: number;
  exchangeRate: number;
};

export type FuturePEMultipleFairValueCalculationOutput = {
  revenueGrowthRatePercentage: number;
};

export const useFuturePEMultipleCalculation = (
  strategyContext: FuturePEMultipleContext
) => {
  const calculateFairValueFromInitialisation =
    useCallback((): FuturePEMultipleCalculationOutput => {
      const calculatorOutputs = {
        fairValue: strategyContext.calculateFairValueToListingCurrency(),
        discountRatePercentage: strategyContext.discountRatePercentage,
        futureSharesOutstanding: strategyContext.futureSharesOutstanding,
        priceEarningsMultiple: strategyContext.futurePE,
        profitMarginPercentage: strategyContext.profitMarginPercentage,
        revenuePercentage: strategyContext.revenueGrowthRatePercentage,
        timePeriod: strategyContext.timeFrame,
        exchangeRate: strategyContext.exchangeRate,
        sharesOutstandingGrowthRate:
          strategyContext.sharesOutstandingGrowthRate,
      };

      return calculatorOutputs;
    }, [strategyContext]);

  const calculateFairValue = useCallback(
    ({
      discountRatePercentage,
      futurePE,
      profitMarginPercentage,
      revenueGrowthRatePercentage,
      timeFrame,
    }: FuturePEMultipleCalculationInput): FuturePEMultipleCalculationOutput => {
      const fairValue = strategyContext
        .setCalculationInputs({
          discountRatePercentage,
          futurePE,
          profitMarginPercentage,
          revenueGrowthRatePercentage,
          timeFrame,
        })
        .calculateFairValueToListingCurrency();

      const calculatorOutputs = {
        fairValue: fairValue,
        futureSharesOutstanding: strategyContext.futureSharesOutstanding,
        discountRatePercentage: strategyContext.discountRatePercentage,
        priceEarningsMultiple: strategyContext.futurePE,
        profitMarginPercentage: strategyContext.profitMarginPercentage,
        revenuePercentage: strategyContext.revenueGrowthRatePercentage,
        timePeriod: strategyContext.timeFrame,
        exchangeRate: strategyContext.exchangeRate,
        sharesOutstandingGrowthRate:
          strategyContext.sharesOutstandingGrowthRate,
      };
      return calculatorOutputs;
    },
    [strategyContext]
  );

  const calculateRevenueGrowthRateFromFairValue = useCallback(
    ({
      discountRatePercentage,
      futurePE,
      profitMarginPercentage,
      timeFrame,
      fairValue,
    }: FuturePEMultipleFairValueCalculationInput): FuturePEMultipleFairValueCalculationOutput => {
      strategyContext
        .setCalculationInputs({
          discountRatePercentage,
          futurePE,
          profitMarginPercentage,
          timeFrame,
          revenueGrowthRatePercentage: 0, // should update this on running setFairValue
        })
        .setFairValue(fairValue);

      return {
        revenueGrowthRatePercentage:
          strategyContext.revenueGrowthRatePercentage,
      };
    },
    [strategyContext]
  );

  const calculateBreakDown = useCallback(
    ({
      discountRatePercentage,
      futurePE,
      profitMarginPercentage,
      revenueGrowthRatePercentage,
      timeFrame,
    }) => {
      return strategyContext
        .setCalculationInputs({
          discountRatePercentage,
          futurePE,
          profitMarginPercentage,
          revenueGrowthRatePercentage,
          timeFrame,
        })
        .calculateBreakDown();
    },
    [strategyContext]
  );

  return {
    calculateFairValueFromInitialisation,
    calculateFairValue,
    calculateRevenueGrowthRateFromFairValue,
    calculateBreakDown,
  };
};
